import type { IncomingCostTarget, IncomingInvoiceKind, IncomingInvoiceStatus } from '@symphenia/shared-types'

export const STATUS_TEXT: Record<IncomingInvoiceStatus, { label: string; klasse: string }> = {
  UNCAPTURED: { label: 'im Eingang', klasse: 'bg-muted text-muted-foreground' },
  DRAFT: { label: 'in Erfassung', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  IN_APPROVAL: { label: 'in Freigabe', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  REJECTED: { label: 'abgelehnt', klasse: 'bg-destructive/15 text-destructive' },
  RELEASED: { label: 'freigegeben', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  EXPORTED: { label: 'übergeben', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  CANCELLED: { label: 'storniert', klasse: 'bg-muted text-muted-foreground line-through' },
}

export const ART_TEXT: Record<IncomingInvoiceKind, string> = {
  INVOICE: 'Rechnung',
  DOWN_PAYMENT_INVOICE: 'Anzahlungsrechnung',
  PARTIAL_INVOICE: 'Teilrechnung',
  FINAL_INVOICE: 'Schlussrechnung',
  CREDIT_NOTE: 'Gutschrift',
}

/** „Projekt" kommt aus `useTerminology()` — deshalb eine Funktion, keine Konstante. */
export const kostenzielText = (ziel: IncomingCostTarget, projekt: string): string => (ziel === 'PROJECT' ? projekt : ziel === 'STOCK' ? 'Lager' : 'Gemeinkosten')

/** 10. — Meldungen der Prüfung; `projekt` wieder aus der Begriffsquelle. */
export const MELDUNG_TEXT: Record<string, string> = {
  SUPPLIER_MISSING: 'Lieferant fehlt',
  DUPLICATE: 'Diese Rechnungsnummer gibt es bei diesem Lieferanten schon',
  DUPLICATE_FILE: 'Dieselbe Datei wurde schon erfasst',
  REQUIRED_FIELD: 'Pflichtfeld fehlt',
  COST_TARGET_REQUIRED: 'Kostenziel fehlt',
  ACCOUNT_REQUIRED: 'Sachkonto fehlt',
  TAX_CODE_MISSING: 'Steuercode fehlt',
  SOURCE_EXHAUSTED: 'Die Bestellposition ist schon vollständig verrechnet',
  PAIRING_UNCONFIRMED: 'Zuordnung zur Bestellposition noch nicht bestätigt',
  AI_PROPOSAL_OPEN: 'Der Vorschlag der KI hat noch Offenes',
  NO_ORDER: 'Ohne Bestellung',
  QUANTITY_DEVIATION: 'Menge über der offenen Menge',
  PRICE_DEVIATION: 'Preis über dem vereinbarten',
  UNIT_MISMATCH: 'Andere Einheit als in der Bestellung',
  UNMATCHED_CHARGES: 'Nebenkosten ohne Bestellung über der Freigrenze',
  SUM_MISMATCH: 'Summe laut Rechnung weicht von der gerechneten ab',
  IBAN_CHANGED: 'IBAN weicht vom Lieferanten ab',
  FORMAL_DEFECT: 'Rechnungsmerkmale unvollständig',
  AMOUNT_ABOVE_LIMIT: 'Betrag über der Grenze',
  AGH_NOT_LISTED: 'Subunternehmer nicht auf der HFU-Liste',
  PRICE_BELOW_ORDER: 'Preis unter dem vereinbarten',
  DISCOUNT_EXPIRES_SOON: 'Skontofrist läuft ab',
  LATE_CAPTURE: 'Eingang mehr als 30 Tage nach dem Rechnungsdatum',
  READER_ISSUE: 'Hinweise aus dem Einlesen der E-Rechnung',
  SUBCONTRACTOR_VAT: 'Subunternehmer weist Umsatzsteuer aus — bei Bauleistungen geht die Steuerschuld meist über',
  ACCOUNT_MISSING: 'Sachkonto fehlt — die Übergabe an die Buchhaltung (BMD) braucht es',
}

export const FELD_TEXT: Record<string, string> = {
  supplierInvoiceNumber: 'Rechnungsnummer',
  invoiceDate: 'Rechnungsdatum',
  statedNet: 'Netto laut Rechnung',
  statedGross: 'Brutto laut Rechnung',
  lines: 'mindestens eine Position',
}

export const MERKMAL_TEXT: Record<string, string> = {
  NOT_CONFIRMED: '„Rechnungsmerkmale geprüft" ist nicht angehakt',
  SERVICE_DATE: 'Leistungsdatum fehlt',
  SUPPLIER_VAT_ID: 'UID des Lieferanten fehlt (über 400 € brutto)',
}

export const tagText = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const [j, m, t] = iso.slice(0, 10).split('-')
  return `${t}.${m}.${j}`
}

/** 9.3 — Abweichungen an der Zeile, kurz für die Marke. */
export const ABWEICHUNG_TEXT: Record<string, { label: string; klasse: string }> = {
  NO_SOURCE: { label: 'ohne Bestellposition', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  QUANTITY_EXCEEDS_OPEN: { label: 'Menge über offen', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  PRICE_ABOVE_TOLERANCE: { label: 'Preis höher', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  PRICE_BELOW_ORDER: { label: 'Preis niedriger', klasse: 'bg-muted text-muted-foreground' },
  UNIT_MISMATCH: { label: 'andere Einheit', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  SOURCE_EXHAUSTED: { label: 'schon verrechnet', klasse: 'bg-destructive/15 text-destructive' },
  SOURCE_GONE: { label: 'Bestellung fehlt', klasse: 'bg-destructive/15 text-destructive' },
}

export const ABGLEICH_TEXT: Record<string, { label: string; klasse: string }> = {
  NONE: { label: 'ohne Bestellung', klasse: 'bg-muted text-muted-foreground' },
  MATCHED: { label: 'abgeglichen', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  DEVIATION: { label: 'Abweichung', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
}

/** Menge ohne überflüssige Nullen: „40", „12,5". */
export const mengeText = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })

// ── Freigabe (11.) ──
export const FREIGABEART_TEXT: Record<string, string> = {
  MATCHED: 'abgeglichen — deckt sich mit der Bestellung',
  APPROVED: 'freigegeben im Freigabelauf',
  NO_APPROVAL_REQUIRED: 'freigegeben ohne Freigabelauf',
}

export const AUFGABE_STATUS_TEXT: Record<string, { label: string; klasse: string }> = {
  OPEN: { label: 'offen', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  APPROVED: { label: 'freigegeben', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  REJECTED: { label: 'abgelehnt', klasse: 'bg-destructive/15 text-destructive' },
  WITHDRAWN: { label: 'zurückgezogen', klasse: 'bg-muted text-muted-foreground' },
}

/** Warum der Rückfall-Freigeber die Aufgabe trägt (11.3). */
export const RUECKFALL_TEXT: Record<string, string> = {
  NO_APPROVER: 'niemand eingetragen',
  NOT_ELIGIBLE: 'der Zuständige darf nicht freigeben (Recht oder Platz fehlt)',
  OWN_CAPTURE: 'der Zuständige hat die Rechnung selbst erfasst',
}

/** Wofür der Freigeber zuständig ist (Nachtrag 2, 4.); `projekt` aus der Begriffsquelle. */
export const bereichText = (scopeKind: string, projekt: string): string =>
  scopeKind === 'PROJECT' ? projekt : scopeKind === 'OVERHEAD_CATEGORY' ? 'Gemeinkosten-Kategorie' : scopeKind === 'STOCK' ? 'Lager' : 'ganze Rechnung'

export const FREIGEBERART_TEXT: Record<string, string> = {
  COST_OWNER: 'Kostenverantwortlicher',
  ORDER_CLERK: 'Besteller',
  USER: 'bestimmter Benutzer',
  ROLE: 'Rolle',
}

export const MODUS_TEXT: Record<string, { label: string; hinweis: string }> = {
  DEVIATIONS: { label: 'bei Abweichungen', hinweis: 'Ein Lauf startet, wenn ein Anlass vorliegt und eine aktive Regel ihn nennt.' },
  ALWAYS: { label: 'immer', hinweis: 'Jede Rechnung läuft. Passt keine Regel, gilt die letzte aktive der Reihenfolge.' },
  NEVER: { label: 'nie', hinweis: 'Abschließen gibt sofort frei. Die Anlässe bleiben sichtbar und protokolliert.' },
}

/** 13.3 — was der Übergabe an die Buchhaltung im Modus „kontiert" fehlt. */
const FEHLT: Record<string, string> = { KREDITOR: 'Kreditornummer am Lieferanten fehlt', ACCOUNT: 'Sachkonto an einer Position fehlt' }
/** „Position 3" · „den Positionen 2 und 5" · ab der siebten gezählt statt genannt. */
export function positionenText(nummern: readonly number[]): string {
  if (nummern.length === 1) return `Position ${nummern[0]}`
  const genannt = nummern.slice(0, nummern.length > 7 ? 6 : nummern.length)
  const rest = nummern.length - genannt.length
  return rest ? `den Positionen ${genannt.join(', ')} und ${rest} weiteren` : `den Positionen ${genannt.slice(0, -1).join(', ')} und ${genannt[genannt.length - 1]}`
}
/** Nachtrag 6 (PT-26): was der Übergabe fehlt — beim Sachkonto mit den Positionen, an denen es fehlt. */
export const fehltText = (z: { missing: readonly string[]; missingAccountLines?: readonly number[] }): string =>
  z.missing.map((m) => (m === 'ACCOUNT' && z.missingAccountLines?.length ? `Sachkonto fehlt an ${positionenText(z.missingAccountLines)}` : (FEHLT[m] ?? m))).join(' · ')
